import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import SellIcon from '@mui/icons-material/Sell';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, date, number, object, string } from 'yup';

import {
  ControlledRadioField,
  RadioOption,
} from 'src/common/components/form/controlled/ControlledRadioField';
import {
  ControlledSelectField,
  SelectOption,
} from 'src/common/components/form/controlled/ControlledSelectField';
import EditorFormActions from 'src/common/components/modals/EditorFormActions';
import { LanguageKeys } from 'src/common/constants/languages';
import { formatRegistrationSignupInfo } from 'src/helpers/registrationPrice';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { resolveResourceId } from 'src/helpers/trackMapCompetitionIcons';
import { ICheckin, IFormCheckin } from 'src/interfaces/Checkin';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface CheckinFormProps {
  handleClose?: () => void;
  handleSave: (checkin: IFormCheckin, id?: string) => void;
  selectedApplicant?: IEnrichedParticipant;
  selectedCheckin?: ICheckin;
}

const CheckinForm = ({
  handleClose,
  handleSave,
  selectedCheckin,
  selectedApplicant,
}: CheckinFormProps) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { activeEditionId, competitionsById, dateFormat, distancesById, editionsById } =
    useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const editionId =
    resolveResourceId(selectedCheckin?.edition) ||
    resolveResourceId(selectedApplicant?.registration?.editionId) ||
    activeEditionId;

  const checkinSchema: ObjectSchema<IFormCheckin> = object()
    .shape({
      id: string(),
      participant: string().required(t('form.yup.genericRequired')),
      competition: string().required(t('form.yup.genericRequired')),
      distance: string().required(t('form.yup.genericRequired')),
      edition: string().required(t('form.yup.genericRequired')),
      startTime: date(),
      finishTime: date(),
      duration: number(),
      priceOverride: number(),
    })
    .required();

  const [editingPrice, setEditingPrice] = useState(false);

  const defaultValues = useMemo<Partial<IFormCheckin>>(
    () =>
      selectedCheckin
        ? {
            competition: resolveResourceId(selectedCheckin.competition),
            participant: selectedCheckin.participant?.id || '',
            distance: resolveResourceId(selectedCheckin.distance),
            edition: editionId || resolveResourceId(selectedCheckin.edition),
          }
        : selectedApplicant
          ? {
              competition: resolveResourceId(selectedApplicant.registration?.competitionId),
              participant: selectedApplicant.id,
              distance: resolveResourceId(selectedApplicant.registration?.distanceId),
              edition: editionId,
            }
          : {
              edition: editionId,
            },
    [selectedCheckin, selectedApplicant, editionId],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm<IFormCheckin>({
    resolver: yupResolver(checkinSchema),
    defaultValues,
  });

  const competition = watch('competition');
  const priceOverride = watch('priceOverride');
  const previousCompetitionRef = useRef(defaultValues.competition);

  useEffect(() => {
    reset(defaultValues);
    previousCompetitionRef.current = defaultValues.competition;
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<IFormCheckin> = async checkin => {
    handleSave(checkin, selectedCheckin?.id);
  };

  // Clear distance only when the user changes competition, not on initial prefill.
  useEffect(() => {
    if (previousCompetitionRef.current === competition) {
      return;
    }
    previousCompetitionRef.current = competition;
    if (competition !== defaultValues.competition) {
      setValue('distance', '');
    }
  }, [competition, defaultValues.competition, setValue]);

  if (!editionsById || !editionId || !competitionsById || !distancesById) return null;

  const currentEdition = editionsById[editionId];
  if (!currentEdition) return null;

  const groupPrice =
    typeof currentEdition.groupPrice === 'number' && !Number.isNaN(currentEdition.groupPrice)
      ? currentEdition.groupPrice
      : undefined;

  const baseRegistrationPrice = selectedApplicant?.registration?.price;
  const displayedPrice =
    typeof priceOverride === 'number' && !Number.isNaN(priceOverride)
      ? priceOverride
      : baseRegistrationPrice;

  const signupInfo = formatRegistrationSignupInfo(
    t,
    currentEdition,
    selectedApplicant?.registration?.createdAt,
    dateFormat[currentLang],
  );

  const applyGroupPrice = () => {
    if (groupPrice == null) {
      return;
    }
    setValue('priceOverride', groupPrice);
    setEditingPrice(true);
  };

  const editionCompetitions = currentEdition.competitions ?? [];
  const systemOptions: RadioOption[] = editionCompetitions.map(comp => {
    const compId = resolveResourceId(comp.competitionId);
    return {
      value: compId,
      label: competitionsById[compId]?.i18n[currentLang] ?? compId,
    };
  });

  const competitionDistances = (
    editionCompetitions.find(
      c => resolveResourceId(c.competitionId) === resolveResourceId(competition),
    )?.distances ?? []
  )
    .map(d => resolveResourceId(d))
    .filter(Boolean);

  const registeredDistanceId = resolveResourceId(
    selectedApplicant?.registration?.distanceId ?? selectedCheckin?.distance,
  );
  const registeredCompetitionId = resolveResourceId(
    selectedApplicant?.registration?.competitionId ?? selectedCheckin?.competition,
  );
  const extraDistanceId =
    resolveResourceId(competition) === registeredCompetitionId ? registeredDistanceId : '';
  const allDistanceIds =
    extraDistanceId && !competitionDistances.includes(extraDistanceId)
      ? [...competitionDistances, extraDistanceId]
      : competitionDistances;

  const distanceOptions: SelectOption[] = sortDistanceIds(allDistanceIds, distancesById).flatMap(
    dist => {
      const distance = distancesById[dist];
      if (!distance) return [];
      return [
        {
          label: distance.i18n[currentLang],
          value: dist,
        },
      ];
    },
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <ControlledRadioField<IFormCheckin>
          control={control}
          name="competition"
          margin="dense"
          fullWidth
          label={`${t('checkins.competition')}:`}
          options={systemOptions}
        />

        <ControlledSelectField<IFormCheckin>
          control={control}
          name="distance"
          disabled={!competition}
          fullWidth
          label={t('form.distance')}
          margin="normal"
          options={distanceOptions}
          withEmptyOption
        />

        {errors
          ? Object.keys(errors).map(key => (
              <FormHelperText key={key} error>
                {t('form.keysWithErrors', { key: t(`form.${key}`) })}
              </FormHelperText>
            ))
          : null}
      </DialogContent>
      <EditorFormActions
        justifyContent="space-between"
        onCancel={handleClose}
        cancelLabel={t('base.cancel')}
        leftSlot={
          selectedApplicant && baseRegistrationPrice != null ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
              {signupInfo ? (
                <Typography variant="caption" color="text.secondary">
                  {signupInfo}
                </Typography>
              ) : null}
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                <SellIcon color="secondary" sx={{ mr: 0.5 }} />
                {editingPrice ? (
                  <TextField
                    size="small"
                    type="number"
                    value={displayedPrice ?? ''}
                    autoFocus
                    sx={{ width: 120 }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">{t('base.currency')}</InputAdornment>
                        ),
                        inputProps: { min: 0, step: 1 },
                      },
                    }}
                    onBlur={e => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) setValue('priceOverride', val);
                    }}
                    onChange={e => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) setValue('priceOverride', val);
                    }}
                  />
                ) : (
                  <>
                    <Typography>{`${displayedPrice} ${t('base.currency')}`}</Typography>
                    <IconButton size="small" onClick={() => setEditingPrice(true)} sx={{ ml: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
                {groupPrice != null ? (
                  <Button size="small" onClick={applyGroupPrice} sx={{ ml: 1 }}>
                    {t('checkins.applyGroupPrice')}
                  </Button>
                ) : null}
              </Box>
            </Box>
          ) : undefined
        }
      />
    </form>
  );
};

export default CheckinForm;
